import { useState, useCallback, useEffect, useMemo } from 'react';

import { AppBar, Tabs } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import { useDataTestId, useBootstrapApp } from 'hooks';
import { IndicatorsEnviroment } from 'models';
import { generateRoutes, generateRoutePathById } from 'utils';

import { Tab } from './components';
import { useStyles } from './styles';

function a11yProps(index: string | number) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`,
  };
}

export type Indicator = {
  id: string;
  name: string;
  data: IndicatorsEnviroment[];
};

export function IndicatorsTabs(): JSX.Element {
  const classes = useStyles();
  const location = useLocation();
  const { getTestId } = useDataTestId();
  const { contexts, currentContext, updateIndicator } = useBootstrapApp();

  const [selectedTab, setSelectedTab] = useState<number | string | undefined>(
    () => {
      return currentContext && currentContext?.id ? currentContext.id : 'about';
    },
  );

  const memoRoutePathsById = useMemo(() => {
    const routes = generateRoutes(contexts);
    const pathsById = generateRoutePathById(routes);

    return pathsById;
  }, [contexts]);

  const handleChangeIndicator = useCallback(
    (contextId: string, indicatorId: string) => {
      setSelectedTab(contextId);
      updateIndicator(contextId, indicatorId);
    },
    [updateIndicator],
  );

  const getActiveTabByUrl = useCallback(() => {
    const active = contexts.find(context =>
      location.pathname.includes(context.id),
    );

    setSelectedTab(active ? active.id : currentContext?.id);
  }, [contexts, currentContext?.id, location.pathname]);

  useEffect(() => {
    getActiveTabByUrl();
  }, [getActiveTabByUrl]);

  useEffect(() => {
    setSelectedTab(currentContext?.id);
  }, [currentContext]);

  return (
    <div className={classes.root}>
      <AppBar
        color="transparent"
        position="static"
        elevation={0}
        {...getTestId('context-indicators-app-bar')}
      >
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedTab || 'about'}
          onChange={(_, newValue) => {
            if (newValue === 'about') {
              handleChangeIndicator('about', 'about');
            }

            return null;
          }}
          aria-label="navigation menu"
        >
          {contexts.map(context => (
            <Tab
              wrapped
              showTooltip={context?.id !== 'about'}
              onChangeIndicator={handleChangeIndicator}
              key={context.id}
              label={context.name}
              indicators={context.indicators}
              value={context.id}
              routesPathById={memoRoutePathsById}
              {...a11yProps(context.id)}
              {...getTestId(`tab-${context.id}`)}
            />
          ))}
        </Tabs>
      </AppBar>
    </div>
  );
}
